import InputText from "./InputText";
import Loader from "../../../Components/Loader/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  calculateEstimatedTradePnlDetails,
  formatTrimmedNumber,
  getEstimatedTradePnlMeta,
  getTpSlValidationMessages,
  isValidDecimalInput,
} from "../tradeUtils";
import SVG from "../../../Components/SVG";
import { API_BASE_URL } from "../../../config/api";

const normalizeDecimalInput = (value) =>
  String(value ?? "")
    .replace(/,/g, "")
    .trim();

const toNullableNumber = (value) => {
  const normalized = normalizeDecimalInput(value);

  if (!normalized || normalized === ".") {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatGroupedInteger = (value) =>
  value.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const formatGroupedDecimalInput = (value) => {
  const normalized = normalizeDecimalInput(value);

  if (!normalized) {
    return "";
  }

  const hasDecimal = normalized.includes(".");
  const [integerPart = "", decimalPart = ""] = normalized.split(".");
  const formattedInteger = integerPart ? formatGroupedInteger(integerPart) : "";

  if (!hasDecimal) {
    return formattedInteger;
  }

  if (!integerPart) {
    return `.${decimalPart}`;
  }

  return `${formattedInteger}.${decimalPart}`;
};

export default function TPSLModal({
  onClose,
  ord,
  tp,
  sl,
  tpSlType,
  estimateContext,
  maxPriceDecimals = 4,
}) {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [tpPrice, setTpPrice] = useState(() => toNullableNumber(tp));
  const [slPrice, setSlPrice] = useState(() => toNullableNumber(sl));
  const [tpDisplay, setTpDisplay] = useState(() =>
    formatGroupedDecimalInput(tp),
  );
  const [slDisplay, setSlDisplay] = useState(() =>
    formatGroupedDecimalInput(sl),
  );
  const [isEditing, setIsEditing] = useState(false);
  const tpSlValidationMessages = useMemo(
    () =>
      getTpSlValidationMessages({
        side: estimateContext?.side,
        marketPrice: estimateContext?.marketPrice,
        tpPrice,
        slPrice,
      }),
    [estimateContext, slPrice, tpPrice],
  );
  const hasTpValidationError = !!tpSlValidationMessages.tp;
  const hasSlValidationError = !!tpSlValidationMessages.sl;
  const hasValidationError = hasTpValidationError || hasSlValidationError;
  const estimatedTpPnlDetails = useMemo(() => {
    if (tpPrice === null || hasTpValidationError) return null;

    return calculateEstimatedTradePnlDetails({
      side: estimateContext?.side,
      entryPrice: estimateContext?.entryPrice,
      targetPrice: tpPrice,
      marginUsdt: estimateContext?.marginUsdt,
      leverage: estimateContext?.leverage,
    });
  }, [estimateContext, hasTpValidationError, tpPrice]);
  const estimatedSlPnlDetails = useMemo(() => {
    if (slPrice === null || hasSlValidationError) return null;

    return calculateEstimatedTradePnlDetails({
      side: estimateContext?.side,
      entryPrice: estimateContext?.entryPrice,
      targetPrice: slPrice,
      marginUsdt: estimateContext?.marginUsdt,
      leverage: estimateContext?.leverage,
    });
  }, [estimateContext, hasSlValidationError, slPrice]);
  const estimatedTpPnl = estimatedTpPnlDetails?.pnl ?? null;
  const estimatedSlPnl = estimatedSlPnlDetails?.pnl ?? null;
  const estimatedTpPnlMeta = getEstimatedTradePnlMeta(estimatedTpPnl, {
    liquidated: estimatedTpPnlDetails?.liquidated,
  });
  const estimatedSlPnlMeta = getEstimatedTradePnlMeta(estimatedSlPnl, {
    liquidated: estimatedSlPnlDetails?.liquidated,
  });
  const formatEstimatedPnl = (value) =>
    formatTrimmedNumber(Math.abs(value), {
      useGrouping: true,
      fallback: "-",
      maxFractionDigits: 2,
    });

  const handlePriceChange = (value, setPrice, setDisplay) => {
    const normalized = normalizeDecimalInput(value);

    if (!isValidDecimalInput(normalized, maxPriceDecimals)) {
      return;
    }

    setDisplay(formatGroupedDecimalInput(normalized));
    setPrice(toNullableNumber(normalized));
  };

  const handleEdit = async () => {
    if (isEditing) return;

    if (hasValidationError) return;

    let jsonBody;
    let url;

    if (tpSlType === "order") {
      jsonBody = {
        order_id: ord,
        account_id: accountId,
        take_profit: tpPrice,
        stop_loss: slPrice,
      };
      url = `${API_BASE_URL}trading/modify-order`;
    } else if (tpSlType === "position") {
      jsonBody = {
        position_id: ord,
        account_id: accountId,
        take_profit: tpPrice,
        stop_loss: slPrice,
      };
      url = `${API_BASE_URL}trading/update-position-tp-sl`;
    } else {
      return;
    }

    setIsEditing(true);
    try {
      let response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify(jsonBody),
      });

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify(jsonBody),
        });
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        console.log("Account Data:", data);
      }

      onClose();
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <div className="w-full p-5 rounded-2xl drop-shadow-md bg-zi-bg2-light dark:bg-zi-bg2-dark flex flex-col gap-5">
      <div className="w-full flex items-start justify-between gap-3">
        <span className="font-medium text-zi-text-light dark:text-zi-text-dark">
          ویرایش حد سود / حد ضرر
        </span>
        <span onClick={onClose}>
          <SVG
            name="close"
            classname="stroke-zi-text-light dark:stroke-zi-text-dark size-5 cursor-pointer"
          />
        </span>
      </div>
      <div className="w-full flex flex-col gap-1">
        <div className="w-full flex items-center">
          <span
            onClick={() => {
              setTpPrice(null);
              setTpDisplay("");
            }}
            className={`group shrink-0 overflow-hidden rounded-lg flex justify-start items-center transition-[width,opacity] duration-300 ease-out ${
              tpDisplay
                ? "w-[34px] opacity-100 p-0.5"
                : "w-0 opacity-0 pointer-events-none p-0"
            }`}
          >
            <SVG
              name="trash"
              classname="shrink-0 fill-amber-400 size-5 cursor-pointer"
            />
          </span>
          <InputText
            label="حد سود"
            unit="USDT"
            id="TPModal"
            name="TP"
            haveFlex1={true}
            value={tpDisplay}
            onChange={(e) => {
              handlePriceChange(e.target.value, setTpPrice, setTpDisplay);
            }}
          />
        </div>
        <div className="min-h-4 flex flex-col">
          {estimatedTpPnl !== null && (
            <span
              className={`ps-9 text-xs ${
                estimatedTpPnlMeta?.tone === "profit"
                  ? "text-zi-green-dark"
                  : "text-zi-red-dark"
              }`}
            >
              {estimatedTpPnlMeta?.label}: {formatEstimatedPnl(estimatedTpPnl)}{" "}
              USDT{estimatedTpPnlMeta?.suffix}
            </span>
          )}
          {tpSlValidationMessages.tp && (
            <span className="ps-9 text-xs text-amber-400">
              {tpSlValidationMessages.tp}
            </span>
          )}
        </div>
      </div>
      <div className="w-full flex flex-col gap-1">
        <div className="w-full flex items-center">
          <span
            onClick={() => {
              setSlPrice(null);
              setSlDisplay("");
            }}
            className={`group shrink-0 overflow-hidden rounded-lg flex justify-start items-center transition-[width,opacity] duration-300 ease-out ${
              slDisplay
                ? "w-[34px] opacity-100 p-0.5"
                : "w-0 opacity-0 pointer-events-none p-0"
            }`}
          >
            <SVG
              name="trash"
              classname="shrink-0 fill-amber-400 size-5 cursor-pointer"
            />
          </span>
          <InputText
            label="حد ضرر"
            unit="USDT"
            id="SLModal"
            name="SL"
            haveFlex1={true}
            value={slDisplay}
            onChange={(e) => {
              handlePriceChange(e.target.value, setSlPrice, setSlDisplay);
            }}
          />
        </div>
        <div className="min-h-4 flex flex-col">
          {estimatedSlPnl !== null && (
            <span
              className={`ps-9 text-xs ${
                estimatedSlPnlMeta?.tone === "profit"
                  ? "text-zi-green-dark"
                  : "text-zi-red-dark"
              }`}
            >
              {estimatedSlPnlMeta?.label}: {formatEstimatedPnl(estimatedSlPnl)}{" "}
              USDT{estimatedSlPnlMeta?.suffix}
            </span>
          )}
          {tpSlValidationMessages.sl && (
            <span className="ps-9 text-xs text-amber-400">
              {tpSlValidationMessages.sl}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={handleEdit}
        disabled={isEditing}
        className={`w-full rounded-lg py-1.5 text-zi-text-dark flex items-center justify-center hocus:opacity-85 disabled:cursor-wait ${
          hasValidationError
            ? "bg-gray-600 cursor-not-allowed"
            : "bg-zi-buttonbg-primary"
        }`}
      >
        {isEditing ? <Loader classname="py-3" /> : "ویرایش"}
      </button>
    </div>
  );
}
